// One episode = one agent run against the shared in-memory document.
import { Agent } from "@mastra/core/agent";
import { createGoogleGenerativeAI } from "@ai-sdk/google";
import * as sp from "@lingjingai/screenplay";
import { z } from "zod";
import { buildTools, newContext, type ReviewContext } from "./tools.js";
import { asrMediaBudget, episodeTask, systemPrompt } from "./prompt.js";
import type { AlignmentReport } from "./asr-review.js";
import type { AuditFinding } from "./finding.js";
import type { WatchUsage } from "./media/watch.js";
import { dispatcher } from "./media/net.js";

export interface UsageRow {
  call_key: string; provider: string; model: string;
  input_tokens: number; cached_input_tokens: number;
  output_tokens: number; reasoning_tokens: number;
}

export interface UnitResult {
  episode_number: number;
  state: "completed" | "failed";
  message: string;
  findings: AuditFinding[];
  error: string | null;
  wall_ms: number;
  steps: number;
  media_calls: number;
  usage: UsageRow[];
}

export interface AgentConfig {
  apiKey: string;
  model: string;
  mediaModel: string;
  baseUrl?: string;
  maxSteps: number;
  mediaBudget: number;
  timeoutMs: number;
}

/** Series-level context: identical for every episode, so a provider can cache it. */
export function seriesCard(doc: sp.Doc): string {
  return sp.read(doc, "").split("\nepisodes\n")[0].trim();
}

export function neighbourSynopses(doc: sp.Doc, episode: number): string {
  return doc.episodes
    .filter((ep) => Math.abs(ep.no - episode) <= 1 && ep.synopsis)
    .map((ep) => `第 ${ep.no} 集：${ep.synopsis}`)
    .join("\n");
}

function timeoutFinding(episode: number, minutes: number): AuditFinding {
  return {
    id: `episode-${episode}-timeout`,
    category: "other",
    title: "审核超时",
    issue: `第 ${episode} 集审核超过 ${minutes} 分钟`,
    impact: "本集可能仍有未收口的候选",
    resolution: "已中断并保留此前结论",
    outcome: "unresolved",
    confidence: "low",
    anchor: {
      episode_number: episode, episode_id: null, scene_number: null,
      scene_id: null, action_id: null, timestamp: null, end_timestamp: null,
    },
    subject: null, before: null, after: null,
    unresolved_reason: `单集审核超过 ${minutes} 分钟硬超时`,
    evidence: [],
  };
}

export async function reviewUnit(
  doc: sp.Doc, episode: number, video: string | undefined, config: AgentConfig,
  asr?: AlignmentReport,
): Promise<UnitResult> {
  const started = Date.now();
  const mediaBudget = asrMediaBudget(asr, config.mediaBudget);
  const ctx: ReviewContext = newContext({
    doc, episode,
    phase: episode === 0 ? "converge" : "episode",
    ...(video ? { video, geminiKey: config.apiKey } : {}),
    mediaModel: config.mediaModel,
    mediaBudget,
  });

  const google = createGoogleGenerativeAI({
    apiKey: config.apiKey,
    ...(config.baseUrl ? { baseURL: `${config.baseUrl.replace(/\/+$/, "").replace(/\/v1beta$/, "")}/v1beta` } : {}),
    fetch: ((url: any, init: any) => fetch(url, {
      ...init, ...(dispatcher() ? { dispatcher: dispatcher() } : {}),
    })) as unknown as typeof fetch,
  });

  const input = {
    seriesCard: seriesCard(doc), episode,
    ...(video ? { video } : {}),
    mediaBudget,
    neighbours: neighbourSynopses(doc, episode),
    asr,
  };

  const agent = new Agent({
    id: `review-ep${episode}`,
    name: `Script review · episode ${episode}`,
    instructions: systemPrompt(input),
    model: google(config.model) as never,
    tools: buildTools(ctx) as never,
  });

  let state: UnitResult["state"] = "completed";
  let message = "";
  let error: string | null = null;
  let steps = 0;
  const usage: UsageRow[] = [];
  const minutes = Math.round(config.timeoutMs / 60_000);

  try {
    const response: any = await agent.generate(episodeTask(input), {
      maxSteps: config.maxSteps,
      modelSettings: { temperature: 0, timeout: { totalMs: config.timeoutMs } },
      structuredOutput: {
        schema: z.object({ message: z.string().describe("一两句话总结本集审核结果") }),
        errorStrategy: "warn",
      },
    } as never);
    steps = response?.steps?.length ?? 0;
    message = response?.object?.message || response?.text?.trim() || `第 ${episode} 集审核完成`;
    const u = response?.usage ?? {};
    usage.push({
      call_key: `review:ep${episode}:agent`,
      provider: "google-gemini", model: config.model,
      input_tokens: Number(u.inputTokens ?? u.promptTokens ?? 0),
      cached_input_tokens: Number(u.cachedInputTokens ?? 0),
      output_tokens: Number(u.outputTokens ?? u.completionTokens ?? 0),
      reasoning_tokens: Number(u.reasoningTokens ?? 0),
    });
  } catch (thrown) {
    const detail = thrown instanceof Error ? thrown.message : String(thrown);
    // A timeout must not discard findings already recorded — that was the worst
    // part of being SIGKILLed mid-episode under codex.
    const timedOut = /timeout|aborted|AbortError/i.test(detail);
    state = timedOut ? "completed" : "failed";
    error = detail.slice(0, 300);
    message = timedOut
      ? `审核在 ${minutes} 分钟超时后中断，已保留此前结论`
      : `第 ${episode} 集审核失败`;
    if (timedOut) ctx.findings.push(timeoutFinding(episode, minutes));
  }

  for (const media of ctx.usage as WatchUsage[]) {
    usage.push({
      call_key: media.call_key, provider: media.provider, model: media.model,
      input_tokens: media.input_tokens, cached_input_tokens: media.cached_input_tokens,
      output_tokens: media.output_tokens, reasoning_tokens: media.reasoning_tokens,
    });
  }

  return {
    episode_number: episode, state, message, error,
    findings: ctx.findings, wall_ms: Date.now() - started,
    steps, media_calls: ctx.mediaCalls, usage,
  };
}
